import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { nasAuthService } from '../services/nasAuthService';

// 사용자 정보 타입
export interface User {
  id: string;
  username: string;
  email: string;
  role: 'ADMIN' | 'STAFF' | 'USER';
  profile?: {
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
  };
}

// 인증 상태 타입
interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// 인증 액션 타입
interface AuthActions {
  login: (username: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  clearError: () => void;
  updateUser: (user: Partial<User>) => void;
  checkAuthStatus: () => Promise<void>;
}

// 초기 상태
const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

// Zustand 스토어 생성
export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      // 로그인 함수
      login: async (username: string, password: string, rememberMe = false) => {
        set({ isLoading: true, error: null });

        try {
          // NAS 인증 서비스 호출
          const authResult = await nasAuthService.login(username, password);

          // 인증 결과 검증
          if (!authResult.success || !authResult.token || !authResult.user) {
            throw new Error('인증 응답이 올바르지 않습니다.');
          }

          // 인증 성공 시 상태 업데이트 - NASUser를 User 타입으로 변환
          const user: User = {
            id: authResult.user.username, // NAS에서는 username을 id로 사용
            username: authResult.user.username,
            email: authResult.user.email,
            role: authResult.user.isAdmin ? 'ADMIN' : 'USER',
            profile: {
              firstName: authResult.user.username,
              lastName: '',
              phoneNumber: ''
            }
          };
          
          set({
            user,
            token: authResult.token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          // Remember Me가 체크되지 않았다면 세션 스토리지만 사용
          if (!rememberMe) {
            // 로컬 스토리지에서 제거하고 세션 스토리지에 저장
            localStorage.removeItem('auth-storage');
            sessionStorage.setItem('auth-session', JSON.stringify({
              user: user, // 변환된 User 객체 사용
              token: authResult.token,
              isAuthenticated: true,
            }));
          }

        } catch (error) {
          const errorMessage = error instanceof Error 
            ? error.message 
            : '로그인 중 오류가 발생했습니다';
          
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
          });

          // 에러를 다시 던져서 UI에서 적절히 처리할 수 있도록 함
          throw error;
        }
      },

      // 로그아웃 함수
      logout: () => {
        // NAS 세션 종료
        nasAuthService.logout();

        // 스토어 상태 초기화
        set(initialState);

        // 저장된 데이터 제거
        localStorage.removeItem('auth-storage');
        sessionStorage.removeItem('auth-session');
      },

      // 토큰 갱신 함수
      refreshToken: async () => {
        const { token } = get();
        
        if (!token) {
          throw new Error('토큰이 없습니다');
        }

        try {
          set({ isLoading: true });
          
          const newToken = await nasAuthService.refreshToken(token);
          
          set({ 
            token: newToken,
            isLoading: false,
            error: null 
          });

        } catch (error) {
          // 토큰 갱신 실패 시 로그아웃
          get().logout();
          throw error;
        }
      },

      // 오류 클리어
      clearError: () => set({ error: null }),

      // 사용자 정보 업데이트
      updateUser: (userData: Partial<User>) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, ...userData } });
        }
      },

      // 인증 상태 확인
      checkAuthStatus: async () => {
        const { token } = get();
        
        if (!token) {
          // 세션 스토리지에서 데이터 확인
          const sessionData = sessionStorage.getItem('auth-session');
          if (sessionData) {
            try {
              const parsedData = JSON.parse(sessionData);
              set({
                user: parsedData.user,
                token: parsedData.token,
                isAuthenticated: parsedData.isAuthenticated,
              });
              return;
            } catch (error) {
              console.error('세션 데이터 파싱 오류:', error);
            }
          }
          
          // NAS 서비스에서 세션 확인
          try {
            const nasUser = await nasAuthService.checkSession();
            if (nasUser) {
              // NASUser를 User 타입으로 변환
              const user: User = {
                id: nasUser.username,
                username: nasUser.username,
                email: nasUser.email,
                role: nasUser.isAdmin ? 'ADMIN' : 'USER',
                profile: {
                  firstName: nasUser.username,
                  lastName: '',
                  phoneNumber: ''
                }
              };
              
              set({
                user,
                token: nasAuthService.getToken(),
                isAuthenticated: true,
                isLoading: false
              });
              return;
            }
          } catch (error) {
            console.warn('NAS 세션 확인 실패:', error);
          }
          
          set({ isAuthenticated: false });
          return;
        }

        // 토큰 만료 여부는 JWT의 exp 클레임으로 네트워크 없이도 확실하게 판단할 수 있다.
        // 이 로컬 검사를 우선 신뢰하고, 여기서 걸러지지 않은 위조/폐기 토큰은
        // 실제 API 호출 시 서버가 401을 반환하면 axios 인터셉터(handleUnauthorized)가 처리한다.
        if (!nasAuthService.isAuthenticated()) {
          console.warn('토큰이 만료되어 로그아웃 처리합니다.');
          get().logout();
          return;
        }

        set({
          isAuthenticated: true,
          isLoading: false
        });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// 인증 훅 - 편의 함수들
export const useAuth = () => {
  const store = useAuthStore();
  
  return {
    ...store,
    isAdmin: store.user?.role === 'ADMIN',
    isStaff: store.user?.role === 'STAFF' || store.user?.role === 'ADMIN',
    userName: store.user?.profile?.firstName 
      ? `${store.user.profile.firstName} ${store.user.profile.lastName || ''}`.trim()
      : store.user?.username || '',
  };
};

// API 응답 401 처리 공통 헬퍼 (axios 인터셉터에서 호출)
// 토큰이 만료된 채로 남아있으면 인증 상태를 정리해 ProtectedRoute가 /login으로 돌려보내도록 한다
export const handleUnauthorized = () => {
  const { isAuthenticated, logout } = useAuthStore.getState();
  if (isAuthenticated) {
    console.warn('인증 만료(401) 감지, 로그아웃 처리합니다.');
    logout();
  }
};

// 자동 토큰 갱신을 위한 인터셉터 설정
export const setupTokenRefresh = () => {
  const refreshInterval = 30 * 60 * 1000; // 30분마다 갱신

  setInterval(async () => {
    const { isAuthenticated, refreshToken } = useAuthStore.getState();
    
    if (isAuthenticated) {
      try {
        await refreshToken();
      } catch (error) {
        console.error('자동 토큰 갱신 실패:', error);
      }
    }
  }, refreshInterval);
};